import { Box, Card, Typography, Input, Stack, Button } from '@mui/joy'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
    useIntegrationList,
    useIntegrationMessages,
} from 'api/chat-api-integration'
import { AuthContext } from 'context/Auth'
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useState } from 'react'
import { MessagesRequestProps } from 'types/dataManagement'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import {
    trackPlaygroundGetData,
    trackPlaygroundGetFullAccess,
} from 'api/tracking'
import { useColorScheme } from '@mui/joy/styles'

import playgroundSwagger from './playground-swagger.json'

import SwaggerUI from 'swagger-ui-react'
import 'swagger-ui-react/swagger-ui.css'
import 'custom-swagger.css'
import { User } from 'api/users'

type Props = {
    from: any
    to: any
}

type BodyRule = {
    match: (req: any) => boolean
    apply: (body: any) => any
}

const trackIfNeeded = (req: any, user: User | null) => {
    if (!user) return

    if (req.url.includes('/v1/chats/data/messages')) {
        trackPlaygroundGetData(user, 'messages')
    }

    if (req.url.includes('/v1/chats/data/facet')) {
        trackPlaygroundGetData(user, 'facet')
    }

    if (req.url.includes('/v1/chats/chat/send')) {
        trackPlaygroundGetData(user, 'send')
    }
}

const PlaygroundIntegration = ({ from, to }: Props) => {
    const { act, user } = useContext(AuthContext)

    const { mode } = useColorScheme()

    if (!user?.access) {
        throw new Error('User has no access and cannot fetch messages.')
    }

    const workspaceId = user?.access?.workspaceId
    const userId = user?.access?.internalUserId

    const { data: integrationList, isLoading } = useIntegrationList({
        workspaceId: workspaceId,
        userId: userId,
        limit: 25,
        page: 1,
    })

    const integrationId =
        integrationList?.filter((item) => item.source === 'whatsapp.com')[0]
            ?.id ?? 12345

    const w: any = window

    const withAuth = (req: any, token?: string | null) => {
        if (!token) return req

        return {
            ...req,
            headers: {
                ...req.headers,
                Authorization: `Bearer ${token}`,
            },
        }
    }

    const bodyRules: BodyRule[] = [
        {
            match: (req) =>
                req.method === 'POST' &&
                req.url.includes('/v1/chats/data/messages'),

            apply: (body) => ({
                ...body,
                workspaceId,
                startDateTime: from,
                endDateTime: to,
            }),
        },
        {
            match: (req) =>
                req.method === 'POST' &&
                req.url.includes('/v1/chats/data/facet'),

            apply: (body) => ({
                ...body,
                workspaceId,
                startDateTime: from,
                endDateTime: to,
            }),
        },
        {
            match: (req) =>
                req.method === 'POST' &&
                req.url.includes('/v1/chats/chat/send'),

            apply: (body) => ({
                ...body,
                workspaceId,
                userId: user?.access?.internalUserId,
                integrationId,
            }),
        },
    ]

    const withDynamicBody = (req: any) => {
        if (!req.body) return req

        let body: any

        try {
            body = JSON.parse(req.body)
        } catch {
            return req
        }

        bodyRules.forEach((rule) => {
            if (rule.match(req)) {
                body = rule.apply(body)
            }
        })

        return {
            ...req,
            body: JSON.stringify(body),
        }
    }

    const snippetConfig = {
        requestSnippetsEnabled: true,
        requestSnippets: {
            generators: {
                node_custom: {
                    title: 'Node.js',
                    syntax: 'javascript',
                },
                javascript: {
                    title: 'JavaScript',
                    syntax: 'javascript',
                },
                python: {
                    title: 'Python',
                    syntax: 'python',
                },
            },
        },
    }

    // Plugin for Code Snippet
    const CodeSnippetPlugin = {
        fn: {
            requestSnippetGenerator_node_custom: (request: any) => {
                const urlObj = new URL(request.get('url'))
                const method = request.get('method')
                const headersMap = request.get('headers')

                const headersObj: Record<string, string> = {}
                if (headersMap) {
                    headersMap.forEach((val: string, key: string) => {
                        headersObj[key] = val
                    })
                }

                let body: any = request.get('body')
                if (body && typeof body !== 'string') {
                    body = JSON.stringify(body)
                }

                return `const https = require("https");

const options = {
  method: "${method}",
  hostname: "${urlObj.hostname}",
  path: "${urlObj.pathname}${urlObj.search}",
  headers: ${JSON.stringify(headersObj, null, 2)}
};

const req = https.request(options, res => {
  let data = "";
  res.on("data", chunk => { data += chunk });
  res.on("end", () => { console.log(data) });
});

${body ? `req.write(${JSON.stringify(body)});` : ''}
req.end();`
            },

            requestSnippetGenerator_javascript: (request: any) => {
                const url = request.get('url')
                const method = request.get('method')
                const headersMap = request.get('headers')

                const headersObj: Record<string, string> = {}
                if (headersMap) {
                    headersMap.forEach((val: string, key: string) => {
                        headersObj[key] = val
                    })
                }

                let body: any = request.get('body')
                if (body && typeof body !== 'string') {
                    body = JSON.stringify(body, null, 2)
                }

                return `fetch("${url}", {
  method: "${method}",
  headers: ${JSON.stringify(headersObj, null, 2)}${
      body
          ? `,
  body: JSON.stringify(${body})`
          : ''
  }
})
.then(res => res.json())
.then(data => console.log(data))
.catch(err => console.error(err));`
            },

            requestSnippetGenerator_python: (request: any) => {
                const url = request.get('url')
                const method = request.get('method')
                const headersMap = request.get('headers')

                const headersObj: Record<string, string> = {}
                if (headersMap) {
                    headersMap.forEach((val: string, key: string) => {
                        headersObj[key] = val
                    })
                }

                let body: any = request.get('body')
                let pythonBody = ''

                if (body) {
                    if (typeof body === 'string') {
                        body = JSON.parse(body)
                    }

                    pythonBody = JSON.stringify(body, null, 4)
                        .replace(/\bnull\b/g, 'None')
                        .replace(/\btrue\b/g, 'True')
                        .replace(/\bfalse\b/g, 'False')
                }

                return `import urllib.request
import urllib.error
import json

url = "${url}"
method = "${method}"

headers = ${JSON.stringify(headersObj, null, 4)}${
                    body
                        ? `

payload = ${pythonBody}

data = json.dumps(payload).encode("utf-8")`
                        : `

data = None`
                }

req = urllib.request.Request(
    url,
    data=data,
    headers=headers,
    method=method
)

try:
    with urllib.request.urlopen(req) as response:
        response_body = response.read().decode("utf-8")
        print("Status:", response.status)
        try:
            print(json.loads(response_body))
        except:
            print(response_body)
except urllib.error.HTTPError as e:
    print("HTTP Error:", e.code)
    print(e.read().decode())
except urllib.error.URLError as e:
    print("URL Error:", e.reason)`
            },
        },
    }
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
            }}
            className={``}
        >
            <div className={`swagger-${mode}`}>
                <SwaggerUI
                    key={`${from}-${to}-${workspaceId}-${integrationId}`}
                    spec={playgroundSwagger}
                    requestInterceptor={(req) => {
                        trackIfNeeded(req, user)
                        req = withAuth(req, act)
                        req = withDynamicBody(req)

                        return req
                    }}
                    plugins={[CodeSnippetPlugin]}
                    {...snippetConfig}
                />
                <Button
                    onClick={() => {
                        trackPlaygroundGetFullAccess(user)
                        if (w && w.tidioChatApi) {
                            w.tidioChatApi.messageFromVisitor(
                                "Hi! I'm interested in Pavuk Chat API full access."
                            )
                        }
                    }}
                    sx={{
                        display: 'flex',
                        margin: '30px 0px',
                        marginLeft: 'auto',
                    }}
                >
                    <SupportAgentIcon
                        sx={{
                            marginRight: '8px',
                        }}
                    />{' '}
                    Request full access
                </Button>
            </div>
            {/* <SwaggerUI url="https://petstore.swagger.io/v2/swagger.json" /> */}
        </Box>
    )
}

export default PlaygroundIntegration
